/**
 * Tiny utility — same shape as the customer SPA's ``cn`` helper but
 * standalone (no clsx dep) to keep this scaffold's transitive dep
 * count small. Joins truthy class names with a space.
 */
type ClassValue = string | number | boolean | null | undefined;

export function cn(...args: ClassValue[]): string {
  return args.filter(Boolean).join(" ");
}

/** Format a number with thousands separators for display.
 *  Returns "—" for null/undefined so the SPA never renders "NaN". */
export function fmtNum(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toLocaleString();
}

/** Format USD with cents. Same null-safety as ``fmtNum``. */
export function fmtUsd(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** ISO date → friendly absolute date. Empty string for missing. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

/** ISO timestamp → relative ("3 days ago"). Coarse buckets — we
 *  don't need a full dayjs runtime for the admin dashboard. */
export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "never";
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mon = Math.floor(day / 30);
  if (mon < 12) return `${mon}mo ago`;
  return `${Math.floor(mon / 12)}y ago`;
}
